import PageErrorBoundary from '@components/PageErrorBoundary';
import { useAuth } from '@contexts/AuthContext';
import { useAppSelectors, useAppStore } from '@stores/app/appStore';
import {
  useChatbotStore,
  useChatbotSelectors,
} from '@stores/chatbot/chatbotStore';
import { ChatbotState } from '@stores/chatbot/types';
import { Layout } from 'antd';
import { useEffect, useState } from 'react';
import { Outlet } from 'react-router';
import FeedBackForm from '@components/FeedBackForm';
import Providers from './Providers';
import '../ProtectedLayout/ProtectedLayout.scss';

const MainAppLayout = () => {
  const setSidebarCollapsed = useAppStore((state) => state.setSidebarCollapsed);
  const setIsLeonardoNewOpen = useAppStore(
    (state) => state.setIsLeonardoNewOpen,
  );

  // New chatbot state
  const chatbotState = useChatbotSelectors.state();
  const { openChatbot } = useChatbotStore();

  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);

  // Computed state for compatibility
  const isChatbotOpen =
    chatbotState === ChatbotState.OPEN || chatbotState === ChatbotState.OPENING;

  const { currentUser } = useAuth();

  // Handle initial message integration with new app store
  const leonardoInitialMessage = useAppSelectors.leonardoInitialMessage();

  useEffect(() => {
    if (leonardoInitialMessage) {
      // Open chatbot with initial message using new system
      openChatbot({ initialMessage: leonardoInitialMessage });
      // Clear the initial message from app store
      useAppStore.getState().setLeonardoInitialMessage(null);
      // Sync with old state for compatibility
      setIsLeonardoNewOpen(true);
    }
  }, [leonardoInitialMessage, openChatbot, setIsLeonardoNewOpen]);

  // Sync new chatbot state with old app store for compatibility
  useEffect(() => {
    setIsLeonardoNewOpen(isChatbotOpen);
  }, [isChatbotOpen, setIsLeonardoNewOpen]);

  // Initialize sidebar state based on screen size
  // Only override persisted state on small screens; respect user preference on larger screens
  useEffect(() => {
    const checkScreenSize = () => {
      // Consider screens < 640px (sm breakpoint) as "really small"
      const isSmallScreen = window.innerWidth < 640;
      // Only force collapse on small screens; on larger screens, respect persisted state
      if (isSmallScreen) {
        setSidebarCollapsed(true);
      }
      // On larger screens, don't override - let persisted state from Zustand take precedence
    };

    // Set initial state on mount
    checkScreenSize();

    // Optional: Update on window resize (uncomment if you want responsive behavior)
    // const handleResize = () => {
    //   checkScreenSize();
    // };
    // window.addEventListener('resize', handleResize);
    // return () => window.removeEventListener('resize', handleResize);
  }, [setSidebarCollapsed]);

  useEffect(() => {
    if (currentUser?.isFeedbackRequired) {
      setIsFeedbackFormOpen(true);
    }
  }, [currentUser]);

  return (
    <Providers>
      <div className="protected-layout">
        {/* Main Content Area - becomes narrower when chat is open */}
        <Layout className="content-layout z-0">
          <Layout.Content
            className="content"
          >
            <PageErrorBoundary pageLabel="MainContent">
              <Outlet />
            </PageErrorBoundary>
          </Layout.Content>
        </Layout>

        {/* Masquerade indicator now lives in the sidebar footer (SidebarFooter). */}
        <FeedBackForm
          open={isFeedbackFormOpen && currentUser?.onboard_completed}
          onCancel={() => setIsFeedbackFormOpen(false)}
        />
      </div>
    </Providers>
  );
};

export default MainAppLayout;
